import { keepPreviousData, useQuery } from "@tanstack/react-query";
import apiClient from "@/features/dashboard/api/axios";
import { BestSellingProducts } from "@/features/dashboard/features/best-selling-section/types";

export type BestSkusPeriod =
  | "7 days"
  | "1 month"
  | "3 months"
  | "6 months"
  | "1 year";

// 포이즌 판매량(sales_30d)은 30일 롤링 관측값이라 기간을 못 쪼갠다 —
// source=poizon이면 서버가 period를 무시하고 항상 최근 30일 랭킹을 준다.
export type BestSkusSource = "kream" | "poizon";

// 서버 응답(sale_count 포함). 대시보드 공용 타입엔 sale_count 가 없어서 여기서 넓힌다.
export type BestSku = BestSellingProducts & { sale_count?: number };

export default function useListBestSkus(
  period: BestSkusPeriod,
  source: BestSkusSource = "kream"
) {
  // Suspense 대신 useQuery + keepPreviousData — 기간·소스를 바꿀 때 목록이 통째로 사라졌다
  // 다시 그려지는 대신, 이전 목록을 흐리게 둔 채 새 목록으로 바꾼다.
  // 서버 캐시가 1시간이라 클라이언트도 5분은 다시 묻지 않는다(셀렉트를 왔다 갔다 해도 즉시 전환).
  const { data, isPending, isFetching, isError, refetch } = useQuery({
    queryKey: ["sku-dashboard", "best-skus", "orderable", source, period],
    queryFn: () =>
      apiClient.get("/sku/best", {
        params: { period, is_orderable: true, source },
      }),
    select: (res) =>
      (res.data as BestSku[])
        .filter((p) => p.in_valid_line_sheet || p.in_valid_api_order)
        .slice(0, 100)
        .map((p, idx) => ({ ...p, ranking: idx + 1 })),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
  });
  return { bestSkus: data ?? [], isPending, isFetching, isError, refetch };
}
